/**
 * useDispatchActions - Zentrale Dispatch-basierte State-Setters
 *
 * Extrahiert aus useSocketHandlers.js für bessere Wartbarkeit.
 * Stellt alle dispatch-Wrapper-Funktionen bereit.
 */

import { useCallback, useRef } from 'react';

/**
 * @param {function} dispatch - GameContext dispatch function
 */
export function useDispatchActions(dispatch) {
  // === Shop Actions ===
  const setShowShop = useCallback((value) => {
    dispatch({ type: 'SET_SHOW_SHOP', payload: value });
  }, [dispatch]);

  const setShopJokersPerPlayer = useCallback((value) => {
    dispatch({ type: 'SET_SHOP_JOKERS_PER_PLAYER', payload: value });
  }, [dispatch]);

  const setShopCurrentPlayer = useCallback((value) => {
    dispatch({ type: 'GAME_STATE_UPDATE', payload: { shopCurrentPlayer: value } });
  }, [dispatch]);

  const setShopRerollCount = useCallback((value) => {
    dispatch({ type: 'SET_SHOP_REROLL_COUNT', payload: value });
  }, [dispatch]);

  const setShopJokerRerollCount = useCallback((value) => {
    dispatch({ type: 'SET_SHOP_JOKER_REROLL_COUNT', payload: value });
  }, [dispatch]);

  // === Exchange Actions ===
  const setShowExchange = useCallback((value) => {
    dispatch({ type: 'SET_SHOW_EXCHANGE', payload: value });
  }, [dispatch]);

  const setExchangeInfo = useCallback((value) => {
    dispatch({ type: 'SET_EXCHANGE_INFO', payload: value });
  }, [dispatch]);

  const setCardsPerPlayer = useCallback((value) => {
    dispatch({ type: 'SET_CARDS_PER_PLAYER', payload: value });
  }, [dispatch]);

  const setExchangeCardsDealt = useCallback((value) => {
    dispatch({ type: 'SET_EXCHANGE_CARDS_DEALT', payload: value });
  }, [dispatch]);

  const setExchangeReady = useCallback((value) => {
    if (typeof value === 'function') {
      dispatch({ type: 'SET_EXCHANGE_READY_UPDATER', payload: value });
    } else {
      dispatch({ type: 'GAME_STATE_UPDATE', payload: { exchangeReady: value } });
    }
  }, [dispatch]);

  const setExchangeCurrentPlayer = useCallback((value) => {
    dispatch({ type: 'SET_EXCHANGE_CURRENT_PLAYER', payload: value });
  }, [dispatch]);

  const setExchangeState = useCallback((value) => {
    dispatch({ type: 'SET_EXCHANGE_STATE', payload: value });
  }, [dispatch]);

  // === Ability Actions ===
  const setShieldUses = useCallback((value) => {
    dispatch({ type: 'SET_SHIELD_USES', payload: value });
  }, [dispatch]);

  const setPeekUses = useCallback((value) => {
    dispatch({ type: 'SET_PEEK_USES', payload: value });
  }, [dispatch]);

  const setSealRingUses = useCallback((value) => {
    dispatch({ type: 'SET_SEAL_RING_USES', payload: value });
  }, [dispatch]);

  const setMagnetUses = useCallback((value) => {
    dispatch({ type: 'SET_MAGNET_USES', payload: value });
  }, [dispatch]);

  const setSwapUses = useCallback((value) => {
    dispatch({ type: 'SET_SWAP_USES', payload: value });
  }, [dispatch]);

  const setPeekedCard = useCallback((value) => {
    dispatch({ type: 'SET_PEEKED_CARD', payload: value });
  }, [dispatch]);

  // === UI Actions ===
  const setShowRoundEnd = useCallback((value) => {
    dispatch({ type: 'SHOW_ROUND_END', payload: value });
  }, [dispatch]);

  const setPendingRoundEnd = useCallback((value) => {
    dispatch({ type: 'SET_PENDING_ROUND_END', payload: value });
  }, [dispatch]);

  const setRankings = useCallback((value) => {
    dispatch({ type: 'SET_RANKINGS', payload: value });
  }, [dispatch]);

  // === Last Action Tracking ===
  const lastActionRef = useRef({ action: null, player: null, playSize: 0 });

  const setLastAction = useCallback((action) => {
    lastActionRef.current.action = action;
    dispatch({
      type: 'SET_LAST_ACTION',
      payload: {
        action,
        player: lastActionRef.current.player,
        playSize: lastActionRef.current.playSize
      }
    });
  }, [dispatch]);

  const setLastActionPlayer = useCallback((player) => {
    lastActionRef.current.player = player;
    dispatch({
      type: 'SET_LAST_ACTION',
      payload: {
        action: lastActionRef.current.action,
        player,
        playSize: lastActionRef.current.playSize
      }
    });
  }, [dispatch]);

  const setLastPlaySize = useCallback((playSize) => {
    lastActionRef.current.playSize = playSize;
    dispatch({
      type: 'SET_LAST_ACTION',
      payload: {
        action: lastActionRef.current.action,
        player: lastActionRef.current.player,
        playSize
      }
    });
  }, [dispatch]);

  // === Game State Update ===
  const updateGameState = useCallback((payload) => {
    dispatch({ type: 'GAME_STATE_UPDATE', payload });
  }, [dispatch]);

  // === Legacy Setters (noop - data flows through GAME_STATE_UPDATE) ===
  // These exist for backward compatibility with handlers that expect them
  const noopSetter = useCallback(() => {}, []);

  return {
    // Shop
    setShowShop,
    setShopJokersPerPlayer,
    setShopCurrentPlayer,
    setShopRerollCount,
    setShopJokerRerollCount,

    // Exchange
    setShowExchange,
    setExchangeInfo,
    setCardsPerPlayer,
    setExchangeCardsDealt,
    setExchangeReady,
    setExchangeCurrentPlayer,
    setExchangeState,

    // Abilities
    setShieldUses,
    setPeekUses,
    setSealRingUses,
    setMagnetUses,
    setSwapUses,
    setPeekedCard,

    // UI
    setShowRoundEnd,
    setPendingRoundEnd,
    setRankings,

    // Last Action
    setLastAction,
    setLastActionPlayer,
    setLastPlaySize,

    // Core
    updateGameState,

    // Legacy (noop)
    setGamePhase: noopSetter,
    setSealedCards: noopSetter,
    setShopItems: noopSetter,
    setShopJokers: noopSetter,
    setShopReady: noopSetter,
    setCoins: noopSetter,
    setPoints: noopSetter,
    setMult: noopSetter,
    setJokers: noopSetter,
    setStash: noopSetter,
    setRoundNumber: noopSetter,
    setTrickNumber: noopSetter,
    setRoundStats: noopSetter,
    setMinCard: noopSetter,
    setMyPlayerIndex: noopSetter,
    setIsRoundStarter: noopSetter,
  };
}

export default useDispatchActions;
